/**
 * SoftSkillsConfigTab — v10.7.3
 * Editor visual para `scenario.soft_skills_config`.
 *
 * Permite:
 *  - Configurar interlocutor (nombre, rol, personalidad, estado inicial)
 *  - Añadir/eliminar/reordenar (drag-drop nativo HTML5) etapas
 *  - Por etapa: prompt + N choices con label, respuesta, deltas emocionales,
 *    flags buena_práctica/error_crítico y siguiente etapa
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash, ArrowUp, ArrowDown, DotsSixVertical, CheckCircle, WarningCircle } from '@phosphor-icons/react';

const EMOTION_KEYS = ['trust', 'anxiety', 'anger', 'understanding', 'cooperation', 'rapport', 'emotional_distress', 'satisfaction', 'sadness'];
const EMOTION_LABELS = {
  trust: 'Confianza', anxiety: 'Ansiedad', anger: 'Enojo', understanding: 'Comprensión',
  cooperation: 'Cooperación', rapport: 'Rapport', emotional_distress: 'Distrés',
  satisfaction: 'Satisfacción', sadness: 'Tristeza',
};

function StageCard({ stage, index, total, allStages, onChange, onRemove, onMove, dragProps }) {
  const [expanded, setExpanded] = useState(index === 0);

  const setField = (k, v) => onChange({ ...stage, [k]: v });
  const setChoice = (ci, patch) => {
    const choices = [...(stage.choices || [])];
    choices[ci] = { ...choices[ci], ...patch };
    onChange({ ...stage, choices });
  };
  const addChoice = () => {
    onChange({ ...stage, choices: [...(stage.choices || []), { id: `c${Date.now()}`, label: '', response: { text: '' }, state_delta: {}, next_stage_id: null }] });
  };
  const removeChoice = (ci) => {
    const choices = [...(stage.choices || [])];
    choices.splice(ci, 1);
    onChange({ ...stage, choices });
  };
  const setDelta = (ci, key, val) => {
    const num = val === '' ? undefined : Number(val);
    const choices = [...(stage.choices || [])];
    const cur = { ...(choices[ci].state_delta || {}) };
    if (num === undefined || Number.isNaN(num) || num === 0) delete cur[key];
    else cur[key] = num;
    choices[ci] = { ...choices[ci], state_delta: cur };
    onChange({ ...stage, choices });
  };

  return (
    <div
      className="border border-slate-200 rounded-lg bg-white"
      draggable
      {...dragProps}
      data-testid={`stage-card-${stage.stage_id || index}`}
    >
      <div className="flex items-center gap-2 p-3 border-b border-slate-100 bg-slate-50/50 rounded-t-lg">
        <div className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700" title="Arrastra para reordenar">
          <DotsSixVertical size={18} weight="bold" />
        </div>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex-1 text-left"
        >
          <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700 mr-2">{stage.stage_id || '?'}</span>
          <span className="text-sm font-medium text-slate-900">{stage.prompt?.slice(0, 60) || 'Etapa sin prompt'}{(stage.prompt || '').length > 60 ? '…' : ''}</span>
          <span className="text-[10px] text-slate-500 ml-2">{(stage.choices || []).length} choices</span>
        </button>
        <Button size="icon" variant="ghost" onClick={() => onMove(-1)} disabled={index === 0} className="h-7 w-7"><ArrowUp size={14} /></Button>
        <Button size="icon" variant="ghost" onClick={() => onMove(1)} disabled={index === total - 1} className="h-7 w-7"><ArrowDown size={14} /></Button>
        <Button size="icon" variant="ghost" onClick={onRemove} className="h-7 w-7 text-red-600" data-testid={`stage-remove-${index}`}><Trash size={14} /></Button>
      </div>
      {expanded && (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-1">
              <Label className="text-xs">ID de etapa</Label>
              <Input value={stage.stage_id || ''} onChange={(e) => setField('stage_id', e.target.value)} placeholder="S" data-testid={`stage-id-${index}`} />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs">Prompt (qué se le muestra al alumno)</Label>
              <Textarea rows={2} value={stage.prompt || ''} onChange={(e) => setField('prompt', e.target.value)} placeholder="¿Cómo inicias la conversación?" data-testid={`stage-prompt-${index}`} />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Choices (opciones que el alumno puede elegir)</Label>
              <Button size="sm" variant="outline" onClick={addChoice} data-testid={`add-choice-${index}`}>
                <Plus size={12} className="mr-1" />Añadir choice
              </Button>
            </div>
            {(stage.choices || []).map((c, ci) => (
              <div key={ci} className="p-3 rounded-md border border-slate-100 bg-slate-50/40 space-y-2" data-testid={`choice-row-${index}-${ci}`}>
                <div className="flex items-start gap-2">
                  <span className="text-slate-400 font-mono text-sm mt-1.5">{String.fromCharCode(65 + ci)}.</span>
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px]">Texto que ve el alumno</Label>
                      <Textarea rows={2} value={c.label || ''} onChange={(e) => setChoice(ci, { label: e.target.value })} placeholder="Le pido acompañarme a una sala privada…" data-testid={`choice-label-${index}-${ci}`} />
                    </div>
                    <div>
                      <Label className="text-[10px]">Respuesta del interlocutor</Label>
                      <Textarea rows={2} value={c.response?.text || ''} onChange={(e) => setChoice(ci, { response: { ...(c.response || {}), text: e.target.value } })} placeholder="La Sra. Pérez lo acompaña, se ve tensa pero atenta." />
                    </div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => removeChoice(ci)} className="h-7 w-7 text-red-600 mt-4"><Trash size={12} /></Button>
                </div>

                {/* Flags + next stage */}
                <div className="flex items-center gap-2 flex-wrap pl-6">
                  <Badge
                    className={`cursor-pointer gap-1 ${c.is_good_practice ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}
                    onClick={() => setChoice(ci, { is_good_practice: !c.is_good_practice, is_critical_error: false })}
                  >
                    <CheckCircle size={11} weight={c.is_good_practice ? 'fill' : 'regular'} /> Buena práctica
                  </Badge>
                  <Badge
                    className={`cursor-pointer gap-1 ${c.is_critical_error ? 'bg-red-100 text-red-700 border-red-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}
                    onClick={() => setChoice(ci, { is_critical_error: !c.is_critical_error, is_good_practice: false })}
                  >
                    <WarningCircle size={11} weight={c.is_critical_error ? 'fill' : 'regular'} /> Error crítico
                  </Badge>
                  <div className="flex items-center gap-1 ml-auto">
                    <Label className="text-[10px] text-slate-500">Siguiente:</Label>
                    <Select value={c.next_stage_id || '__end__'} onValueChange={(v) => setChoice(ci, { next_stage_id: v === '__end__' ? null : v })}>
                      <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__end__">Fin</SelectItem>
                        {allStages.filter((s) => s.stage_id && s.stage_id !== stage.stage_id).map((s) => (
                          <SelectItem key={s.stage_id} value={s.stage_id}>{s.stage_id}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Emotional deltas */}
                <div className="pl-6">
                  <Label className="text-[10px] text-slate-500 mb-1 block">Impacto emocional (Δ, entre -30 y +30)</Label>
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-1.5">
                    {EMOTION_KEYS.map((k) => (
                      <div key={k} className="flex flex-col">
                        <span className="text-[9px] text-slate-500">{EMOTION_LABELS[k]}</span>
                        <Input
                          type="number"
                          min={-30} max={30}
                          value={c.state_delta?.[k] ?? ''}
                          onChange={(e) => setDelta(ci, k, e.target.value)}
                          className="h-7 text-xs font-mono"
                          placeholder="0"
                          data-testid={`delta-${index}-${ci}-${k}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            {!(stage.choices || []).length && <div className="text-xs text-slate-400 text-center py-2">Sin choices. Añade al menos una.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SoftSkillsConfigTab({ config, onChange }) {
  const [dragIdx, setDragIdx] = useState(null);

  const cfg = config || {};
  const setCfg = (patch) => onChange({ ...cfg, ...patch });

  const stages = cfg.stages || [];
  const interlocutor = cfg.interlocutor || {};
  const initial = interlocutor.initial_state || {};

  const setInterlocutor = (patch) => setCfg({ interlocutor: { ...interlocutor, ...patch } });
  const setInitial = (k, v) => {
    const num = v === '' ? undefined : Number(v);
    const cur = { ...initial };
    if (num === undefined || Number.isNaN(num)) delete cur[k];
    else cur[k] = num;
    setInterlocutor({ initial_state: cur });
  };

  const setStages = (newStages) => setCfg({ stages: newStages });
  const updateStage = (idx, next) => {
    const s = [...stages];
    s[idx] = next;
    setStages(s);
  };
  const removeStage = (idx) => {
    const s = [...stages];
    s.splice(idx, 1);
    setStages(s);
  };
  const moveStage = (idx, dir) => {
    const s = [...stages];
    const to = idx + dir;
    if (to < 0 || to >= s.length) return;
    [s[idx], s[to]] = [s[to], s[idx]];
    setStages(s);
  };
  const addStage = () => {
    const id = String.fromCharCode(65 + stages.length); // A, B, C...
    setStages([...stages, { stage_id: id, prompt: '', choices: [] }]);
  };

  const onDragStart = (idx) => (e) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch { /* noop */ }
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onDrop = (idx) => (e) => {
    e.preventDefault();
    const from = dragIdx;
    if (from === null || from === idx) return;
    const s = [...stages];
    const [moved] = s.splice(from, 1);
    s.splice(idx, 0, moved);
    setStages(s);
    setDragIdx(null);
  };

  return (
    <div className="space-y-6" data-testid="soft-skills-config-tab">
      {/* Interlocutor */}
      <Card>
        <CardHeader><CardTitle>Interlocutor</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Nombre</Label>
              <Input value={interlocutor.name || ''} onChange={(e) => setInterlocutor({ name: e.target.value })} placeholder="Sra. Ana Pérez" data-testid="interlocutor-name" />
            </div>
            <div>
              <Label className="text-xs">Rol</Label>
              <Input value={interlocutor.role || ''} onChange={(e) => setInterlocutor({ role: e.target.value })} placeholder="familiar_hija" />
            </div>
            <div>
              <Label className="text-xs">Edad</Label>
              <Input type="number" value={interlocutor.age ?? ''} onChange={(e) => setInterlocutor({ age: e.target.value ? Number(e.target.value) : null })} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Personalidad</Label>
            <Input value={interlocutor.personality || ''} onChange={(e) => setInterlocutor({ personality: e.target.value })} placeholder="afectuosa, ansiosa" />
          </div>
          <div>
            <Label className="text-xs">Expectativas</Label>
            <Textarea rows={2} value={interlocutor.expectations || ''} onChange={(e) => setInterlocutor({ expectations: e.target.value })} placeholder="Espera buenas noticias, no anticipa la gravedad." />
          </div>
          <div>
            <Label className="text-xs mb-2 block">Estado emocional inicial (0–100)</Label>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
              {EMOTION_KEYS.map((k) => (
                <div key={k}>
                  <span className="text-[10px] text-slate-500">{EMOTION_LABELS[k]}</span>
                  <Input
                    type="number" min={0} max={100}
                    value={initial[k] ?? ''}
                    onChange={(e) => setInitial(k, e.target.value)}
                    className="h-8 text-xs font-mono"
                    placeholder="50"
                    data-testid={`initial-${k}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Protocol code */}
      <Card>
        <CardHeader><CardTitle>Protocolo asociado</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Código del protocolo (rúbrica)</Label>
              <Input value={cfg.protocol_code || ''} onChange={(e) => setCfg({ protocol_code: e.target.value.toUpperCase() })} placeholder="SPIKES" data-testid="protocol-code" />
              <p className="text-[10px] text-slate-500 mt-1">Debe existir en Mantenedores → Protocolos H. Blandas.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stages */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Etapas y choices</CardTitle>
            <Button size="sm" onClick={addStage} data-testid="add-stage-btn"><Plus size={14} className="mr-1" />Añadir etapa</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {stages.length === 0 && (
            <div className="text-center py-8 text-slate-500 text-sm">
              Sin etapas. Añade la primera para comenzar el flujo de diálogo.
            </div>
          )}
          {stages.map((s, i) => (
            <div
              key={i}
              onDragOver={onDragOver(i)}
              onDrop={onDrop(i)}
            >
              <StageCard
                stage={s}
                index={i}
                total={stages.length}
                allStages={stages}
                onChange={(ns) => updateStage(i, ns)}
                onRemove={() => removeStage(i)}
                onMove={(dir) => moveStage(i, dir)}
                dragProps={{ onDragStart: onDragStart(i) }}
              />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
